import {
  RiFlashlightLine,
  RiIdCardLine,
  RiLeafLine,
  RiSettings4Line,
  RiShoppingBagLine,
} from "@remixicon/react";
import { Link, Outlet, Route } from "react-router-dom";

import { BriefCrumb } from "@/pages/BriefBuilderAssistantPage/components/BriefCrumb.tsx";
import { OrderItemBreadcrumb } from "@/pages/OrderItemPage/OrderItemBreadcrumb";
import { OrderBreadcrumb } from "@/pages/OrdersPage/OrderBreadcrumb";
import { tolgee } from "@/providers/TranslationProvider";
import { OrderItemPage } from "../OrderItemPage";
import { DefaultOmsRoute } from "./DefaultRoute";
import { OmsLayout } from "./layouts/OmsLayout";
import {
  AccountPage,
  BriefAssistantPage,
  DashboardPage,
  OmsEcoScoringFormPage,
  OmsInboxPage,
  OmsModificationRequestPage,
  OmsOrderDetailsAddQuoteFormPage,
  OmsOrderPage,
  OmsOrdersPage,
  OmsOrderTrackingDetailsAddRetroplanningFormPage,
  OmsOrganisationalEntitiesPage,
  OmsSettingsFormPage,
  OpenBriefPage,
  SentBriefPage,
} from "./lazyPages";

export const omsRoutes = (
  <Route key="oms-routes" path="/oms" element={<OmsLayout />}>
    <Route
      path="account"
      element={<AccountPage />}
      handle={{
        crumb: () => (
          <Link to="/account">{tolgee.t("navigation.order.account")}</Link>
        ),
      }}
    />

    <Route path="" index element={<DefaultOmsRoute />} />
    <Route
      path="dashboard"
      element={<DashboardPage />}
      handle={{
        crumb: () => (
          <Link to="/oms/dashboard" className="flex items-center gap-sp-base">
            <RiFlashlightLine className="size-sp-md" />
            {tolgee.t("navigation.dashboard")}
          </Link>
        ),
      }}
    />

    {/* Inbox Page */}
    <Route path="inbox" element={<OmsInboxPage />} />

    <Route
      path="orders"
      handle={{
        crumb: () => (
          <Link to="/orders" className="flex items-center gap-sp-base">
            <RiShoppingBagLine className="size-sp-md" />
            {tolgee.t("navigation.order.myOrders")}
          </Link>
        ),
      }}
    >
      <Route index element={<OmsOrdersPage />} />
      <Route
        path=":orderId"
        element={<Outlet />}
        loader={({ params }) => {
          return params;
        }}
        handle={{
          crumb: (data: { orderId: string }) => {
            return <OrderBreadcrumb orderId={data.orderId} isOms />;
          },
        }}
      >
        <Route
          path="order-items/:orderItemId"
          loader={({ params }) => {
            return params;
          }}
          handle={{
            crumb: (data: { orderItemId: string; orderId: string }) => {
              return (
                <OrderItemBreadcrumb
                  orderId={data.orderId}
                  orderItemId={data.orderItemId}
                  isOms
                />
              );
            },
          }}
          element={<Outlet />}
        >
          <Route index element={<OrderItemPage />} />
          <Route
            path="add-quote"
            element={<OmsOrderDetailsAddQuoteFormPage />}
          />
          <Route
            path="add-retroplanning"
            element={<OmsOrderTrackingDetailsAddRetroplanningFormPage />}
          />
          <Route path="edit" element={<OmsModificationRequestPage />} />
        </Route>
        <Route index element={<OmsOrderPage />} />
      </Route>
    </Route>

    {/* Settings */}
    <Route
      path="settings"
      element={<OmsSettingsFormPage />}
      handle={{
        crumb: () => (
          <Link to="/oms/settings" className="flex items-center gap-sp-base">
            <RiSettings4Line className="size-sp-md" />
            {tolgee.t("oms.featureFlags.title")}
          </Link>
        ),
      }}
    />

    {/* Ids */}
    <Route
      path="organisational-entities"
      element={<OmsOrganisationalEntitiesPage />}
      handle={{
        crumb: () => (
          <Link
            to="/oms/organisational-entities"
            className="flex items-center gap-sp-base"
          >
            <RiIdCardLine className="size-sp-md" />
            {tolgee.t("oms.ids.title")}
          </Link>
        ),
      }}
    />

    {/* EcoScoring */}
    <Route
      path="eco-scoring"
      element={<OmsEcoScoringFormPage />}
      handle={{
        crumb: () => (
          <Link to="/oms/eco-scoring" className="flex items-center gap-sp-base">
            <RiLeafLine className="size-sp-md" />
            {tolgee.t("oms.updateEcoScore.title")}
          </Link>
        ),
      }}
    />
    <Route
      path="open-brief"
      element={<Outlet />}
      handle={{
        crumb: () => (
          <Link to="/open-brief">{tolgee.t("navigation.openBrief")}</Link>
        ),
      }}
    >
      <Route index element={<OpenBriefPage />} />
      <Route
        path="assistant"
        element={<BriefAssistantPage />}
        handle={{
          crumb: () => (
            <Link to="/open-brief/assistant">
              {tolgee.t("briefBuilderAssistant.title")}
            </Link>
          ),
        }}
      />
      <Route
        path="assistant/:briefId"
        element={<BriefAssistantPage />}
        handle={{
          crumb: () => <BriefCrumb to="/open-brief/assistant/:briefId" />,
        }}
      />
      <Route
        path=":briefId"
        element={<SentBriefPage />}
        handle={{
          crumb: () => <BriefCrumb to="/open-brief/:briefId" />,
        }}
      />
    </Route>
  </Route>
);
